import { HttpStatusCode } from "axios";
import { JobServiceApi } from "../serverConnectors";
import {
  EmployerCountByCategory,
  AvailableJobLocation,
  Job,
  JobCountByCategory,
  JobCountByCategoryAndLocation,
  SearchFilterQuery,
} from "../types";

export interface JobServiceInterface {
  searchJob(
    searchFilter: SearchFilterQuery,
  ): Promise<{ jobs: Job[]; count: number }>;
  adminJobSearch(
    searchFilterQuery: SearchFilterQuery,
  ): Promise<{ jobs: Job[]; count: number }>;
  getJobCountByCategory(countryName: string): Promise<JobCountByCategory[]>;
  getEmployerCountByCategory(
    countryName: string,
  ): Promise<EmployerCountByCategory[]>;
  findJobIndustriesByCountry(countryName: string): Promise<string[]>;
  findEmployersByCountry(countryName: string): Promise<string[]>;
  findAvailableJobLocationsByCountry(
    countryName: string,
  ): Promise<AvailableJobLocation[]>;
  findJobEmploymentTypesByCountry(countryName: string): Promise<string[]>;
  findAllJobsCount(): Promise<number>;
  findAllEmployersCount(): Promise<number>;
  findAllIndustryCount(): Promise<number>;
  findJobCountByCountry(): Promise<{ country: string; jobCount: number }[]>;
  findJobCountByRegion(
    country: string,
  ): Promise<{ country: string; region: string; jobCount: number }[]>;
  findJobCountByCity(
    country: string,
    region: string,
  ): Promise<
    { country: string; region: string; city: string; jobCount: number }[]
  >;
  findAllIndustries(): Promise<string[]>;
  findJobBySenderReference(senderReference: string): Promise<Job[]>;
  findJobCountByCategoryAndLocation(location: {
    country?: string;
    region?: string;
    city?: string;
  }): Promise<JobCountByCategoryAndLocation[]>;
}

interface JobSearchApiResponse {
  data?: Job[];
  count?: number;
  error?: string;
}

export class JobService extends JobServiceApi implements JobServiceInterface {
  async searchJob(
    searchFilter: SearchFilterQuery,
  ): Promise<{ jobs: Job[]; count: number }> {
    return this.executeSearch("/job/search", searchFilter, "searchJob");
  }

  async adminJobSearch(
    searchFilterQuery: SearchFilterQuery,
  ): Promise<{ jobs: Job[]; count: number }> {
    return this.executeSearch(
      "/job/admin/search",
      searchFilterQuery,
      "adminJobSearch",
    );
  }

  private async executeSearch(
    endpoint: string,
    searchFilter: SearchFilterQuery,
    operation: string,
  ): Promise<{ jobs: Job[]; count: number }> {
    try {
      const response = await this.axios.post<JobSearchApiResponse>(
        endpoint,
        searchFilter,
      );
      if (response.status === HttpStatusCode.Ok) {
        return {
          jobs: Array.isArray(response.data.data) ? response.data.data : [],
          count:
            typeof response.data.count === "number" ? response.data.count : 0,
        };
      }
    } catch (error: unknown) {
      console.error(`JobService::${operation}`, error);
    }
    return { jobs: [], count: 0 };
  }

  async getJobCountByCategory(
    countryName: string,
  ): Promise<JobCountByCategory[]> {
    let jobCountByCategories: JobCountByCategory[] = [];
    try {
      const response = await this.axios.get(
        `/job/find/count/category/${countryName}`,
      );
      if (response.status === HttpStatusCode.Ok) {
        jobCountByCategories = response.data.data;
      }
    } catch (error: any) {
      console.error("JobService::getJobCountByCategory", error);
    }
    return jobCountByCategories;
  }

  async getEmployerCountByCategory(
    countryName: string,
  ): Promise<EmployerCountByCategory[]> {
    let employerCountByCategories: EmployerCountByCategory[] = [];
    try {
      const response = await this.axios.get(
        `/job/find/count/employer/category/${countryName}`,
      );
      if (response.status === HttpStatusCode.Ok) {
        employerCountByCategories = response.data.data;
      }
    } catch (error: any) {
      console.error("JobService::getEmployerCountByCategory", error);
    }
    return employerCountByCategories;
  }

  async findJobIndustriesByCountry(countryName: string): Promise<string[]> {
    let categories: string[] = [];
    try {
      const response = await this.axios.get(`/job/category/${countryName}`);
      if (response.status === HttpStatusCode.Ok) {
        categories = response.data.data.map(
          (item: { category: string }) => item.category,
        );
      }
    } catch (error: any) {
      console.error("JobService::findJobIndustriesByCountry", error);
    }
    return categories;
  }

  async findEmployersByCountry(countryName: string): Promise<string[]> {
    let employers: string[] = [];
    try {
      const response = await this.axios.get(`/job/employers/${countryName}`);
      if (response.status === HttpStatusCode.Ok) {
        employers = response.data.data.map(
          (item: { employer: string }) => item.employer,
        );
      }
    } catch (error: any) {
      console.error("JobService::findEmployersByCountry", error);
    }
    return employers;
  }

  async findAvailableJobLocationsByCountry(
    countryName: string,
  ): Promise<AvailableJobLocation[]> {
    let locations: AvailableJobLocation[] = [];
    try {
      const response = await this.axios.get(`/job/locations/${countryName}`);
      if (response.status === HttpStatusCode.Ok) {
        locations = response.data.data;
      }
    } catch (error: any) {
      console.error("JobService::findAvailableJobLocationsByCountry", error);
    }
    return locations;
  }

  async findJobEmploymentTypesByCountry(
    countryName: string,
  ): Promise<string[]> {
    let employmentTypes: string[] = [];
    try {
      const response = await this.axios.get(
        `/job/employment-type/${countryName}`,
      );
      if (response.status === HttpStatusCode.Ok) {
        employmentTypes = response.data.data.map(
          (item: { employment_type: string }) => item.employment_type,
        );
      }
    } catch (error: any) {
      console.error("JobService::findJobEmploymentTypesByCountry", error);
    }
    return employmentTypes;
  }

  async findAllJobsCount(): Promise<number> {
    let jobCount: number = 0;
    try {
      const response = await this.axios.get(`job/find/count/all`);
      if (response.status === HttpStatusCode.Ok) {
        jobCount = response.data.data[0].jobCount;
      }
    } catch (error: any) {
      console.error("JobService::findAllJobsCount", error);
    }
    return jobCount;
  }

  async findAllEmployersCount(): Promise<number> {
    let employersCount: number = 0;
    try {
      const response = await this.axios.get(`job/find/count/all-employer`);
      if (response.status === HttpStatusCode.Ok) {
        employersCount = response.data.data[0].employerCount;
      }
    } catch (error: any) {
      console.error("JobService::findAllEmployersCount", error);
    }
    return employersCount;
  }

  async findAllIndustryCount(): Promise<number> {
    let industryCount: number = 0;
    try {
      const response = await this.axios.get(`job/find/count/all-industry`);
      if (response.status === HttpStatusCode.Ok) {
        industryCount = response.data.data[0].industryCount;
      }
    } catch (error: any) {
      console.error("JobService::findAllIndustryCount", error);
    }
    return industryCount;
  }

  async findJobCountByCountry(): Promise<
    {
      country: string;
      jobCount: number;
    }[]
  > {
    let jobCountByCountry: { country: string; jobCount: number }[] = [];
    try {
      const response = await this.axios.get(`job/find/count/country`);
      if (response.status === HttpStatusCode.Ok) {
        jobCountByCountry = response.data.data.map(
          (item: { country: string; job_count: number }) => ({
            country: item.country,
            jobCount: item.job_count,
          }),
        );
      }
    } catch (error: any) {
      console.error("JobService::findJobCountByCountry", error);
    }
    return jobCountByCountry;
  }

  async findJobCountByRegion(country: string): Promise<
    {
      country: string;
      region: string;
      jobCount: number;
    }[]
  > {
    let jobCountByRegion: {
      country: string;
      region: string;
      jobCount: number;
    }[] = [];
    try {
      const response = await this.axios.get(`job/find/count/region/${country}`);
      if (response.status === HttpStatusCode.Ok) {
        jobCountByRegion = response.data.data.map(
          (item: { country: string; region: string; job_count: number }) => ({
            country: item.country,
            region: item.region,
            jobCount: item.job_count,
          }),
        );
      }
    } catch (error: any) {
      console.error("JobService::findJobCountByRegion", error);
    }
    return jobCountByRegion;
  }

  async findJobCountByCity(
    country: string,
    region: string,
  ): Promise<
    {
      country: string;
      region: string;
      city: string;
      jobCount: number;
    }[]
  > {
    let jobCountByCity: {
      country: string;
      region: string;
      city: string;
      jobCount: number;
    }[] = [];
    try {
      const response = await this.axios.get(
        `job/find/count/city/${country}/${region}`,
      );
      if (response.status === HttpStatusCode.Ok) {
        jobCountByCity = response.data.data.map(
          (item: {
            country: string;
            region: string;
            city: string;
            job_count: number;
          }) => ({
            country: item.country,
            region: item.region,
            city: item.city,
            jobCount: item.job_count,
          }),
        );
      }
    } catch (error: any) {
      console.error("JobService::findJobCountByCity", error);
    }
    return jobCountByCity;
  }

  async findAllIndustries(): Promise<string[]> {
    let industries: string[] = [];
    try {
      const response = await this.axios.get(`job/category`);
      if (response.status === HttpStatusCode.Ok) {
        industries = response.data.data.map(
          (item: { category: string }) => item.category,
        );
      }
    } catch (error: any) {
      console.error("JobService::findAllIndustries", error);
    }
    return industries;
  }

  async findJobBySenderReference(senderReference: string): Promise<Job[]> {
    try {
      const response = await this.axios.get(`job/find/sr/${encodeURIComponent(senderReference)}`);
      if (response.status !== HttpStatusCode.Ok || !Array.isArray(response.data?.data)) {
        throw new Error("Invalid job lookup response");
      }
      return response.data.data;
    } catch (error: any) {
      console.error("JobService::findJobBySenderReference", error);
      // A temporary API failure must not mark an existing job as removed/noindex.
      throw error;
    }
  }

  async findJobCountByCategoryAndLocation(location: {
    country?: string;
    region?: string;
    city?: string;
  }): Promise<JobCountByCategoryAndLocation[]> {
    let jobCountByCategoryAndLocation: JobCountByCategoryAndLocation[] = [];
    try {
      const response = await this.axios.get(
        `/job/find/count/category-by-location`,
        { params: location },
      );
      if (response.status === HttpStatusCode.Ok) {
        jobCountByCategoryAndLocation = response.data.data.map((item: any) => ({
          country: item.country,
          region: item.region,
          city: item.city,
          category: item.category,
          jobCount: item.job_count,
        }));
      }
    } catch (error: any) {
      console.error("JobService::findJobCountByCategoryAndLocation", error);
    }
    return jobCountByCategoryAndLocation;
  }
}
